/**
 * +field: the main quest's waypoint (docs/design/zones.md "directed main quest"): a gold beacon over
 * the objective (or the portal on the way) with its distance, and, when it is off screen, an arrow
 * on the screen edge pointing at it. Reads fieldView.waypoint (worldApp sets it on every refresh).
 */
import type { HudUpdate } from '../hud';
import type { HudWidget } from '../widgets';
import { el, setText } from '../dom';
import { fieldView } from '../../world/fieldView';
import { edgeArrow } from '../../world/waypointModel';

/** Within this many yards the beacon fades out (you are there). */
const ARRIVED_YD = 3.5;

export class WaypointMarker implements HudWidget {
  private readonly beacon: HTMLElement;
  private readonly label: HTMLElement;
  private readonly dist: HTMLElement;
  private readonly arrow: HTMLElement;
  private shown: 'beacon' | 'arrow' | null = null;
  private version = -1;
  private lastDist = -1;

  constructor(hudRoot: HTMLElement) {
    this.beacon = el('div', 'fx-wp hk-hidden', hudRoot);
    el('div', 'fx-wp-gem', this.beacon);
    this.label = el('div', 'fx-wp-label', this.beacon);
    this.dist = el('div', 'fx-wp-dist hk-num', this.beacon);
    this.arrow = el('div', 'fx-wp-arrow hk-hidden', hudRoot);
    el('div', 'fx-wp-arrow-head', this.arrow);
  }

  update(h: HudUpdate): void {
    const v = fieldView;
    const wp = v.active ? v.waypoint : null;
    const me = h.playerId ? h.world.units[h.playerId] : undefined;
    if (!wp || !me) return this.show(null);
    if (v.waypointVersion !== this.version) {
      this.version = v.waypointVersion;
      setText(this.label, wp.label);
      this.beacon.classList.toggle('portal', wp.portal);
    }
    const d = Math.hypot(wp.pos.x - me.pos.x, wp.pos.y - me.pos.y);
    if (d < ARRIVED_YD && !wp.portal) return this.show(null);
    const yd = Math.round(d);
    if (yd !== this.lastDist) {
      this.lastDist = yd;
      setText(this.dist, `${yd} yd`);
    }
    const sp = h.renderer.project(wp.pos, 2.2);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (sp.visible && sp.x > 40 && sp.x < vw - 40 && sp.y > 60 && sp.y < vh - 120) {
      this.beacon.style.transform = `translate(${sp.x.toFixed(1)}px, ${sp.y.toFixed(1)}px)`;
      return this.show('beacon');
    }
    // Off screen: an arrow on the edge, toward the target (projected point, or the hero → target
    // direction turned into screen space when the target is behind the camera).
    const hero = h.renderer.project(me.pos, 1);
    const a = edgeArrow({ x: hero.x, y: hero.y }, sp, vw, vh);
    this.arrow.style.transform = `translate(${a.x.toFixed(1)}px, ${a.y.toFixed(1)}px) rotate(${a.angle.toFixed(3)}rad)`;
    this.show('arrow');
  }

  private show(which: 'beacon' | 'arrow' | null): void {
    if (which === this.shown) return;
    this.shown = which;
    this.beacon.classList.toggle('hk-hidden', which !== 'beacon');
    this.arrow.classList.toggle('hk-hidden', which !== 'arrow');
  }

  dispose(): void {
    this.beacon.remove();
    this.arrow.remove();
  }
}
